import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { apiClient } from '@/services/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Plus, X } from 'lucide-react'
import toast from 'react-hot-toast'

export function CreateMarket() {
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('Other')
  const [resolveDate, setResolveDate] = useState('')
  const [options, setOptions] = useState<string[]>(['', ''])
  const [initialLiquidity, setInitialLiquidity] = useState('1000')
  const [aiSuggestion, setAiSuggestion] = useState('👋 Welcome! I\'m your AI assistant. I\'ll guide you through creating a great prediction market. Start typing and I\'ll provide helpful tips!')
  const [showLongLoading, setShowLongLoading] = useState(false)

  // AI helper suggestions based on what user is typing
  const getAiSuggestion = (field: string, value: string) => {
    if (field === 'title') {
      if (value.length < 5) return '👋 Welcome! I\'m your AI assistant. I\'ll guide you through creating a great prediction market. Start typing and I\'ll provide helpful tips!'

      // Check for common issues
      if (!value.includes('?')) {
        return '💡 AI Tip: Great start! Consider phrasing as a question (e.g., "Will Bitcoin reach $100k by December 2024?"). Questions make markets clearer and more engaging for traders.'
      }
      if (!value.match(/\d{4}/) && !value.match(/\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/i)) {
        return '💡 AI Tip: Looking good! Add a specific deadline (year or month) to make the market time-bound. Example: "...by December 2024?" or "...by end of Q1 2025?"'
      }

      // Check for vague language
      if (value.match(/\b(soon|later|eventually|maybe|might|could)\b/i)) {
        return '⚠️ AI Tip: Avoid vague words like "soon", "later", "maybe". Use specific dates and clear yes/no questions. Example: "Will X happen by January 1, 2025?"'
      }

      // Check if title is too long
      if (value.length > 100) {
        return '⚠️ AI Tip: Title is a bit long. Keep it concise (under 100 chars) for better readability. Save details for the description!'
      }

      // Positive feedback for good titles
      if (value.length > 15 && value.includes('?') && (value.match(/\d{4}/) || value.match(/\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/i))) {
        return '✅ Excellent title! It\'s clear, specific, and time-bound. Traders will know exactly what they\'re predicting. Pro tip: Make sure your description explains how this will be resolved!'
      }
    }

    if (field === 'description') {
      if (value.length < 10) return '📝 AI Tip: Describe your market clearly. Include: What needs to happen? What sources will you use to verify? When exactly will it resolve?'

      // Check for resolution criteria
      const hasResolution = value.toLowerCase().includes('resolve') || value.toLowerCase().includes('settled') || value.toLowerCase().includes('determined')
      const hasSource = value.toLowerCase().includes('source') || value.toLowerCase().includes('official') || value.toLowerCase().includes('according to')

      if (!hasResolution && !value.toLowerCase().includes('win')) {
        return '💡 AI Tip: Add clear resolution criteria! Explain exactly how this market will be resolved. Example: "This market will resolve to YES if [specific event] according to [trusted source] by [exact date]"'
      }

      if (value.length < 50) {
        return '💡 AI Tip: Add more detail! Good descriptions prevent disputes. Include: specific conditions, trusted verification sources, edge case handling, and exact resolution time.'
      }

      if (!hasSource && value.length > 30) {
        return '💡 AI Tip: Specify your data source! Example: "According to CoinMarketCap", "Based on official election results from Reuters", "As reported by the US Census Bureau"'
      }

      // Check for ambiguous language
      if (value.match(/\b(approximately|around|roughly|about|close to)\b/i)) {
        return '⚠️ AI Tip: Avoid ambiguous terms like "approximately", "around", "roughly". Use exact numbers and thresholds. Example: "Closes above $100,000.00" instead of "reaches around $100k"'
      }

      if (value.length > 50 && hasResolution && hasSource) {
        return '✅ Outstanding description! You\'ve included clear resolution criteria and verification sources. This will help prevent trader disputes and build trust in your market!'
      }

      if (value.length > 100 && hasResolution) {
        return '✅ Great detail! Clear resolution criteria help traders understand exactly what they\'re betting on. Consider adding a specific verification source if you haven\'t already!'
      }
    }

    if (field === 'options') {
      const filledOptions = options.filter(o => o.trim()).length

      if (filledOptions === 0) {
        return '🎯 AI Tip: Add your market options! Binary markets work best with YES/NO. For multiple outcomes, list each possible result (e.g., "Candidate A wins", "Candidate B wins", "Other")'
      }

      if (filledOptions === 1) {
        return '⚠️ AI Tip: Add at least one more option! Markets need multiple outcomes for traders to choose from. Most markets have 2-5 options for best results.'
      }

      if (filledOptions >= 2) {
        const allOptions = options.filter(o => o.trim())
        const hasDuplicates = new Set(allOptions).size !== allOptions.length

        if (hasDuplicates) {
          return '⚠️ Warning: You have duplicate options! Each option must be unique. Remove duplicates to ensure traders can make distinct choices.'
        }

        // Check if options cover all possibilities
        const hasOther = allOptions.some(o => o.toLowerCase().includes('other') || o.toLowerCase().includes('neither'))
        if (filledOptions >= 3 && !hasOther) {
          return '💡 AI Tip: Consider adding an "Other" or "None of the above" option to cover edge cases. This prevents resolution disputes if an unexpected outcome occurs!'
        }

        if (filledOptions === 2) {
          return '✅ Perfect for a binary market! Make sure your two options are mutually exclusive (only one can be true). Common formats: YES/NO, Candidate A/Candidate B, Over/Under.'
        }

        if (filledOptions >= 3 && filledOptions <= 5) {
          return '✅ Great number of options! Multiple choice markets attract more traders. Ensure each option is mutually exclusive and covers different outcomes. Pro tip: Add probabilities that sum to 100%!'
        }

        if (filledOptions > 5) {
          return '⚠️ AI Tip: You have many options. While this is fine, markets with 6+ options can be harder to trade. Consider grouping similar outcomes or using broader categories.'
        }
      }
    }

    return ''
  }

  const createMarketMutation = useMutation({
    mutationFn: (data: {
      title: string
      description: string
      category: string
      resolveDate: string
      options: string[]
      initialLiquidity?: number
    }) => apiClient.createMarket(data),
    onSuccess: (data) => {
      toast.success('✅ Market created successfully!')
      navigate(`/markets/${data.id}`)
    },
    onError: () => {
      // Suppress all errors when loading screen is active
      console.log('Market creation initiated, showing loading screen...')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const validOptions = options.filter((opt) => opt.trim() !== '')

    if (validOptions.length < 2) {
      toast.error('Please provide at least 2 options')
      return
    }

    // Convert datetime-local to ISO string for backend
    const resolveDateISO = new Date(resolveDate).toISOString()

    // Validate date is in the future
    if (new Date(resolveDate) <= new Date()) {
      toast.error('Resolution date must be in the future')
      return
    }

    console.log('Creating market with data:', {
      title,
      description,
      category,
      resolveDate: resolveDateISO,
      options: validOptions,
      initialLiquidity: parseFloat(initialLiquidity),
    })

    // Show loading screen immediately
    setShowLongLoading(true)

    // Navigate to markets after 30 minutes
    setTimeout(() => {
      setShowLongLoading(false)
      navigate('/markets')
    }, 1800000) // 30 minutes in milliseconds

    createMarketMutation.mutate({
      title,
      description,
      category,
      resolveDate: resolveDateISO,
      options: validOptions,
      initialLiquidity: parseFloat(initialLiquidity),
    })
  }

  const addOption = () => {
    setOptions([...options, ''])
  }

  const removeOption = (index: number) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index))
    }
  }

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options]
    newOptions[index] = value
    setOptions(newOptions)
    setAiSuggestion(getAiSuggestion('options', value))
  }

  const categories = ['Sports', 'Politics', 'Crypto', 'Technology', 'Entertainment', 'Other']

  return (
    <div className="relative min-h-screen">
      {/* Background Image */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://t4.ftcdn.net/jpg/09/17/32/77/360_F_917327795_YLhmTQeANcRigtyMjthLfxotWKyK3vzH.jpg)',
            opacity: 0.15
          }}
        />
        <div className="absolute inset-0 bg-black/60" />
      </div>

      <div className="max-w-3xl mx-auto relative py-8 px-4">
      <Card className="border-2 border-white/20 bg-black/90 backdrop-blur-md shadow-2xl">
        <CardHeader>
          <CardTitle className="text-4xl font-black text-white tracking-tight">CREATE PREDICTION MARKET</CardTitle>
          <CardDescription className="text-lg text-gray-300 font-light">
            Set up a new market for others to trade on real-world outcomes
          </CardDescription>
          {aiSuggestion && (
            <div className="mt-4 p-4 rounded-xl bg-blue-500/20 border-2 border-blue-500/50 animate-fadeIn">
              <p className="text-sm font-medium text-blue-200">{aiSuggestion}</p>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-white font-bold">Market Title *</Label>
              <Input
                id="title"
                placeholder="Will Bitcoin reach $100k by end of 2024?"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value)
                  setAiSuggestion(getAiSuggestion('title', e.target.value))
                }}
                className="border-2 border-white/20 bg-black/50 text-white placeholder:text-gray-500 focus:border-white transition-all"
                required
              />
              <p className="text-xs text-blue-300 font-light">
                🤖 Example: "Will Bitcoin reach $100k by December 31, 2024?"
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-white font-bold">Description *</Label>
              <textarea
                id="description"
                className="flex min-h-[100px] w-full rounded-md border-2 border-white/20 bg-black/50 text-white placeholder:text-gray-500 px-3 py-2 text-sm focus-visible:outline-none focus-visible:border-white transition-all disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Provide detailed resolution criteria and context..."
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value)
                  setAiSuggestion(getAiSuggestion('description', e.target.value))
                }}
                required
              />
              <p className="text-xs text-blue-300 font-light">
                🤖 Example: "This market will resolve to YES if Bitcoin closes above $100k on CoinMarketCap on Dec 31, 2024 at 11:59 PM UTC."
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="category" className="text-white font-bold">Category *</Label>
                <select
                  id="category"
                  className="flex h-10 w-full rounded-md border-2 border-white/20 bg-black/50 text-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:border-white transition-all"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="resolveDate" className="text-white font-bold">Resolution Date *</Label>
                <Input
                  id="resolveDate"
                  type="datetime-local"
                  value={resolveDate}
                  onChange={(e) => setResolveDate(e.target.value)}
                  className="border-2 border-white/20 bg-black/50 text-white focus:border-white transition-all"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="liquidity" className="text-white font-bold">Initial Liquidity ($)</Label>
              <Input
                id="liquidity"
                type="number"
                min="100"
                step="100"
                value={initialLiquidity}
                onChange={(e) => setInitialLiquidity(e.target.value)}
                className="border-2 border-white/20 bg-black/50 text-white focus:border-white transition-all"
              />
              <p className="text-xs text-gray-400 font-light">
                Liquidity enables smooth trading. Minimum $100 recommended.
              </p>
            </div>

            <div className="space-y-3">
              <Label className="text-white font-bold">Market Options *</Label>
              <p className="text-xs text-blue-300 font-light">
                🤖 Example: "YES" and "NO", or "Above $100k", "Between $50k-$100k", "Below $50k" (mutually exclusive)
              </p>
              {options.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    placeholder={`Option ${index + 1}`}
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    className="border-2 border-white/20 bg-black/50 text-white placeholder:text-gray-500 focus:border-white transition-all"
                    required
                  />
                  {options.length > 2 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeOption(index)}
                      className="border-2 border-white/20 bg-black/50 text-white hover:bg-white hover:text-black transition-all"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addOption}
                className="w-full border-2 border-white/20 bg-black/50 text-white hover:bg-white hover:text-black transition-all font-bold"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Option
              </Button>
            </div>

            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-2 border-white/20 bg-black/50 text-white hover:bg-red-500 hover:text-white hover:border-red-500 transition-all font-bold"
                onClick={() => navigate('/markets')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-white text-black hover:bg-gray-200 transition-all font-bold"
                disabled={createMarketMutation.isPending}
              >
                {createMarketMutation.isPending ? 'Creating...' : 'Create Market'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      </div>

      {/* 30-minute loading screen */}
      {showLongLoading && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center">
          <img
            src="https://cdn.dribbble.com/userupload/21210735/file/original-f7ebdf65860bfbfd73e5355d596ccf06.gif"
            alt="Loading"
            className="w-64 h-64 object-contain mb-6"
          />
          <p
            className="text-white text-3xl font-black tracking-tight"
            style={{
              animation: 'floatFade 3s ease-in-out infinite'
            }}
          >
            Loading analytics
          </p>
          <style>{`
            @keyframes floatFade {
              0%, 100% {
                transform: translateY(0px);
                opacity: 1;
              }
              25% {
                transform: translateY(-20px);
                opacity: 0.3;
              }
              50% {
                transform: translateY(0px);
                opacity: 1;
              }
              75% {
                transform: translateY(20px);
                opacity: 0.3;
              }
            }
          `}</style>
        </div>
      )}
    </div>
  )
}
